'use client'

import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
    MdTimer,
    MdLogin,
    MdLogout,
    MdCoffee,
    MdCheck,
    MdLock,
} from 'react-icons/md'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import ConfirmModal from '@/components/confirm-modal'
import SearchableSelect from '@/components/searchable-select'
import {
    clockIn,
    clockOut,
    breakOut,
    breakIn,
    getTimeEntries,
    getLastEntry,
    getTimeSheets,
    closeTimeSheet,
    approveTimeSheet,
    HrTimeEntry,
    HrTimeSheet,
} from '@/lib/api/hr/hr-time-tracking-api'
import {
    getHrEmployees,
    HrEmployee,
} from '@/lib/api/hr/hr-employee-api'

const TYPE_LABELS: Record<string, string> = {
    CLOCK_IN: 'Entrada',
    BREAK_OUT: 'Saída Intervalo',
    BREAK_IN: 'Retorno Intervalo',
    CLOCK_OUT: 'Saída',
}

const STATUS_BADGES: Record<string, string> = {
    OPEN: 'badge-info',
    CLOSED: 'badge-warning',
    APPROVED: 'badge-success',
}

const STATUS_LABELS: Record<string, string> = {
    OPEN: 'Aberta',
    CLOSED: 'Fechada',
    APPROVED: 'Aprovada',
}

const formatTime = (dateStr: string) => {
    const d = new Date(dateStr)
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR')
}

const minutesToDisplay = (minutes: number) => {
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    return `${h}h ${String(m).padStart(2, '0')}min`
}

export default function TimeTrackingPage() {
    const { canCreate, canUpdate } = useResourcePermissions('hr', 'time_tracking')

    const [employees, setEmployees] = useState<HrEmployee[]>([])
    const [loading, setLoading] = useState(true)

    // Registrar Ponto
    const [selectedEmployeeId, setSelectedEmployeeId] = useState('')
    const [lastEntry, setLastEntry] = useState<HrTimeEntry | null>(null)
    const [clockLoading, setClockLoading] = useState(false)
    const [notes, setNotes] = useState('')

    // Espelho de Ponto
    const [mirrorEmployeeId, setMirrorEmployeeId] = useState('')
    const [mirrorMonth, setMirrorMonth] = useState(new Date().getMonth() + 1)
    const [mirrorYear, setMirrorYear] = useState(new Date().getFullYear())
    const [entries, setEntries] = useState<HrTimeEntry[]>([])
    const [entriesLoading, setEntriesLoading] = useState(false)

    // Folhas de Ponto
    const [sheetsMonth, setSheetsMonth] = useState(new Date().getMonth() + 1)
    const [sheetsYear, setSheetsYear] = useState(new Date().getFullYear())
    const [timeSheets, setTimeSheets] = useState<HrTimeSheet[]>([])
    const [sheetsLoading, setSheetsLoading] = useState(false)

    // Confirmação
    const [confirmAction, setConfirmAction] = useState<{
        type: 'close' | 'approve'
        id: number
        title: string
        message: string
    } | null>(null)
    const [confirmLoading, setConfirmLoading] = useState(false)

    useEffect(() => {
        loadEmployees()
    }, [])

    useEffect(() => {
        if (selectedEmployeeId) {
            loadLastEntry(parseInt(selectedEmployeeId, 10))
        } else {
            setLastEntry(null)
        }
    }, [selectedEmployeeId])

    const loadEmployees = async () => {
        setLoading(true)
        try {
            const res = await getHrEmployees({ limit: 500 })
            if (res.success && res.result) {
                setEmployees(res.result || [])
            }
        } catch {
            toast.error('Erro ao carregar funcionários')
        } finally {
            setLoading(false)
        }
    }

    const loadLastEntry = async (employeeId: number) => {
        try {
            const res = await getLastEntry(employeeId)
            if (res.success) {
                setLastEntry(res.result)
            }
        } catch {
            // silencioso
        }
    }

    const handleClock = async (action: 'clock-in' | 'clock-out' | 'break-out' | 'break-in') => {
        if (!selectedEmployeeId) {
            toast.warning('Selecione um funcionário')
            return
        }

        setClockLoading(true)
        try {
            const payload = {
                employeeId: parseInt(selectedEmployeeId, 10),
                notes: notes || undefined,
            }

            let res
            switch (action) {
                case 'clock-in':
                    res = await clockIn(payload)
                    break
                case 'clock-out':
                    res = await clockOut(payload)
                    break
                case 'break-out':
                    res = await breakOut(payload)
                    break
                case 'break-in':
                    res = await breakIn(payload)
                    break
            }

            if (res.success) {
                toast.success(`Ponto registrado: ${TYPE_LABELS[res.result.type]}`)
                setLastEntry(res.result)
                setNotes('')
            } else {
                toast.error(res.error || 'Erro ao registrar ponto')
            }
        } catch {
            toast.error('Erro ao registrar ponto')
        } finally {
            setClockLoading(false)
        }
    }

    const loadEntries = async () => {
        if (!mirrorEmployeeId) {
            toast.warning('Selecione um funcionário')
            return
        }

        setEntriesLoading(true)
        try {
            const res = await getTimeEntries({
                employeeId: parseInt(mirrorEmployeeId, 10),
                month: mirrorMonth,
                year: mirrorYear,
            })
            if (res.success && res.result) {
                setEntries(res.result)
            }
        } catch {
            toast.error('Erro ao carregar registros')
        } finally {
            setEntriesLoading(false)
        }
    }

    const loadTimeSheets = async () => {
        setSheetsLoading(true)
        try {
            const res = await getTimeSheets({ month: sheetsMonth, year: sheetsYear })
            if (res.success && res.result) {
                setTimeSheets(res.result)
            }
        } catch {
            toast.error('Erro ao carregar folhas de ponto')
        } finally {
            setSheetsLoading(false)
        }
    }

    const handleCloseSheet = async () => {
        if (!confirmAction || confirmAction.type !== 'close') return
        setConfirmLoading(true)
        try {
            const res = await closeTimeSheet(confirmAction.id)
            if (res.success) {
                toast.success('Folha de ponto fechada com sucesso')
                setConfirmAction(null)
                loadTimeSheets()
            } else {
                toast.error(res.error || 'Erro ao fechar folha de ponto')
            }
        } catch {
            toast.error('Erro ao fechar folha de ponto')
        } finally {
            setConfirmLoading(false)
        }
    }

    const handleApproveSheet = async () => {
        if (!confirmAction || confirmAction.type !== 'approve') return
        setConfirmLoading(true)
        try {
            const res = await approveTimeSheet(confirmAction.id)
            if (res.success) {
                toast.success('Folha de ponto aprovada com sucesso')
                setConfirmAction(null)
                loadTimeSheets()
            } else {
                toast.error(res.error || 'Erro ao aprovar folha de ponto')
            }
        } catch {
            toast.error('Erro ao aprovar folha de ponto')
        } finally {
            setConfirmLoading(false)
        }
    }

    // Agrupar entradas por data para o espelho de ponto
    const entriesByDate = entries.reduce<Record<string, HrTimeEntry[]>>((acc, entry) => {
        const dateKey = new Date(entry.timestamp).toLocaleDateString('pt-BR')
        if (!acc[dateKey]) acc[dateKey] = []
        acc[dateKey].push(entry)
        return acc
    }, {})

    const calculateDayWorkedMinutes = (dayEntries: HrTimeEntry[]) => {
        let total = 0
        let periodStart: Date | null = null
        const sorted = [...dayEntries].sort(
            (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
        )
        for (const entry of sorted) {
            if (entry.type === 'CLOCK_IN' || entry.type === 'BREAK_IN') {
                periodStart = new Date(entry.timestamp)
            } else if (
                (entry.type === 'BREAK_OUT' || entry.type === 'CLOCK_OUT') &&
                periodStart
            ) {
                total += Math.round(
                    (new Date(entry.timestamp).getTime() - periodStart.getTime()) / 60000,
                )
                periodStart = null
            }
        }
        return total
    }

    const totalMirrorMinutes = Object.values(entriesByDate).reduce(
        (sum, dayEntries) => sum + calculateDayWorkedMinutes(dayEntries),
        0,
    )
    const totalMirrorOvertime = Object.values(entriesByDate).reduce((sum, dayEntries) => {
        const worked = calculateDayWorkedMinutes(dayEntries)
        return sum + Math.max(0, worked - 480)
    }, 0)

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-8">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                    <MdTimer className="text-2xl text-white" />
                </div>
                <div>
                    <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                        Ponto Eletrônico
                    </h1>
                    <p className="text-sm text-base-content/60">
                        Controle de jornada de trabalho
                    </p>
                </div>
            </div>

            {/* Seção 1: Registrar Ponto */}
            {canCreate && (
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">Registrar Ponto</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Funcionário</span>
                                </label>
                                <SearchableSelect
                                    options={employees.map(emp => ({
                                        value: String(emp.id),
                                        label: emp.name,
                                        description: emp.department || undefined,
                                    }))}
                                    value={selectedEmployeeId}
                                    onChange={value => setSelectedEmployeeId(value)}
                                    placeholder="Selecione um funcionário"
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Observações</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Observações (opcional)"
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                />
                            </div>
                        </div>

                        {lastEntry && (
                            <div className="mb-4 p-3 bg-base-200/50 rounded-lg">
                                <p className="text-sm text-base-content/70">
                                    Último registro:{' '}
                                    <span className="font-semibold">
                                        {TYPE_LABELS[lastEntry.type]}
                                    </span>{' '}
                                    em{' '}
                                    <span className="font-mono">
                                        {formatDate(lastEntry.timestamp)} {formatTime(lastEntry.timestamp)}
                                    </span>
                                </p>
                            </div>
                        )}

                        <div className="flex flex-wrap gap-3">
                            <button
                                className="btn btn-success gap-2"
                                onClick={() => handleClock('clock-in')}
                                disabled={clockLoading || !selectedEmployeeId}
                            >
                                {clockLoading ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    <MdLogin className="text-xl" />
                                )}
                                Entrada
                            </button>

                            <button
                                className="btn btn-warning gap-2"
                                onClick={() => handleClock('break-out')}
                                disabled={clockLoading || !selectedEmployeeId}
                            >
                                {clockLoading ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    <MdCoffee className="text-xl" />
                                )}
                                Saída Intervalo
                            </button>

                            <button
                                className="btn btn-info gap-2"
                                onClick={() => handleClock('break-in')}
                                disabled={clockLoading || !selectedEmployeeId}
                            >
                                {clockLoading ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    <MdCoffee className="text-xl" />
                                )}
                                Retorno Intervalo
                            </button>

                            <button
                                className="btn btn-error gap-2"
                                onClick={() => handleClock('clock-out')}
                                disabled={clockLoading || !selectedEmployeeId}
                            >
                                {clockLoading ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    <MdLogout className="text-xl" />
                                )}
                                Saída
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Seção 2: Espelho de Ponto */}
            <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                <div className="card-body">
                    <h2 className="card-title text-lg mb-4">Espelho de Ponto</h2>

                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Funcionário</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={mirrorEmployeeId}
                                onChange={e => setMirrorEmployeeId(e.target.value)}
                            >
                                <option value="">Selecione</option>
                                {employees.map(emp => (
                                    <option key={emp.id} value={emp.id}>
                                        {emp.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Mês</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={mirrorMonth}
                                onChange={e => setMirrorMonth(parseInt(e.target.value, 10))}
                            >
                                {Array.from({ length: 12 }, (_, i) => (
                                    <option key={i + 1} value={i + 1}>
                                        {new Date(2000, i).toLocaleDateString('pt-BR', { month: 'long' })}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Ano</span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full"
                                value={mirrorYear}
                                onChange={e => setMirrorYear(parseInt(e.target.value, 10))}
                                min={2020}
                                max={2100}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">&nbsp;</span>
                            </label>
                            <button
                                className="btn btn-secondary w-full"
                                onClick={loadEntries}
                                disabled={entriesLoading || !mirrorEmployeeId}
                            >
                                {entriesLoading ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    'Consultar'
                                )}
                            </button>
                        </div>
                    </div>

                    {entries.length > 0 && (
                        <>
                            <div className="overflow-x-auto">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Data</th>
                                            <th>Entrada</th>
                                            <th>Saída Intervalo</th>
                                            <th>Retorno Intervalo</th>
                                            <th>Saída</th>
                                            <th className="text-right">Horas Trabalhadas</th>
                                            <th className="text-right">Hora Extra</th>
                                            <th>Observações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(entriesByDate).map(([date, dayEntries]) => {
                                            const clockInEntry = dayEntries.find(e => e.type === 'CLOCK_IN')
                                            const breakOutEntry = dayEntries.find(e => e.type === 'BREAK_OUT')
                                            const breakInEntry = dayEntries.find(e => e.type === 'BREAK_IN')
                                            const clockOutEntry = dayEntries.find(e => e.type === 'CLOCK_OUT')
                                            const worked = calculateDayWorkedMinutes(dayEntries)
                                            const overtime = Math.max(0, worked - 480)
                                            const entryNotes = dayEntries
                                                .filter(e => e.notes)
                                                .map(e => e.notes)
                                                .join('; ')

                                            return (
                                                <tr key={date} className="hover">
                                                    <td className="font-mono text-sm">{date}</td>
                                                    <td className="font-mono text-sm">
                                                        {clockInEntry ? formatTime(clockInEntry.timestamp) : '-'}
                                                    </td>
                                                    <td className="font-mono text-sm">
                                                        {breakOutEntry ? formatTime(breakOutEntry.timestamp) : '-'}
                                                    </td>
                                                    <td className="font-mono text-sm">
                                                        {breakInEntry ? formatTime(breakInEntry.timestamp) : '-'}
                                                    </td>
                                                    <td className="font-mono text-sm">
                                                        {clockOutEntry ? formatTime(clockOutEntry.timestamp) : '-'}
                                                    </td>
                                                    <td className="text-right font-mono text-sm">
                                                        {minutesToDisplay(worked)}
                                                    </td>
                                                    <td className="text-right font-mono text-sm">
                                                        {overtime > 0 ? (
                                                            <span className="text-warning font-semibold">
                                                                {minutesToDisplay(overtime)}
                                                            </span>
                                                        ) : (
                                                            '-'
                                                        )}
                                                    </td>
                                                    <td className="text-sm text-base-content/60 max-w-[200px] truncate">
                                                        {entryNotes || '-'}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr className="font-semibold">
                                            <td colSpan={5} className="text-right">
                                                Totais:
                                            </td>
                                            <td className="text-right font-mono">
                                                {minutesToDisplay(totalMirrorMinutes)}
                                            </td>
                                            <td className="text-right font-mono text-warning">
                                                {totalMirrorOvertime > 0
                                                    ? minutesToDisplay(totalMirrorOvertime)
                                                    : '-'}
                                            </td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </>
                    )}

                    {entries.length === 0 && mirrorEmployeeId && !entriesLoading && (
                        <div className="text-center py-8 text-base-content/50">
                            Nenhum registro encontrado para o período selecionado
                        </div>
                    )}
                </div>
            </div>

            {/* Seção 3: Folhas de Ponto */}
            <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                <div className="card-body">
                    <h2 className="card-title text-lg mb-4">Folhas de Ponto</h2>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Mês</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={sheetsMonth}
                                onChange={e => setSheetsMonth(parseInt(e.target.value, 10))}
                            >
                                {Array.from({ length: 12 }, (_, i) => (
                                    <option key={i + 1} value={i + 1}>
                                        {new Date(2000, i).toLocaleDateString('pt-BR', { month: 'long' })}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Ano</span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full"
                                value={sheetsYear}
                                onChange={e => setSheetsYear(parseInt(e.target.value, 10))}
                                min={2020}
                                max={2100}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">&nbsp;</span>
                            </label>
                            <button
                                className="btn btn-secondary w-full"
                                onClick={loadTimeSheets}
                                disabled={sheetsLoading}
                            >
                                {sheetsLoading ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    'Consultar'
                                )}
                            </button>
                        </div>
                    </div>

                    {timeSheets.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Funcionário</th>
                                        <th>Período</th>
                                        <th className="text-right">Horas Trabalhadas</th>
                                        <th className="text-right">Hora Extra</th>
                                        <th className="text-right">Adicional Noturno</th>
                                        <th className="text-center">Faltas</th>
                                        <th className="text-center">Atrasos</th>
                                        <th>Status</th>
                                        <th>Aprovado Por</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {timeSheets.map(sheet => (
                                        <tr key={sheet.id} className="hover">
                                            <td>
                                                <div className="font-semibold">
                                                    {sheet.employee?.name || '-'}
                                                </div>
                                                {sheet.employee?.department && (
                                                    <div className="text-xs text-base-content/60">
                                                        {sheet.employee.department}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="font-mono text-sm">
                                                {String(sheet.month).padStart(2, '0')}/{sheet.year}
                                            </td>
                                            <td className="text-right font-mono text-sm">
                                                {minutesToDisplay(sheet.totalWorkedMinutes)}
                                            </td>
                                            <td className="text-right font-mono text-sm">
                                                {sheet.totalOvertimeMinutes > 0 ? (
                                                    <span className="text-warning font-semibold">
                                                        {minutesToDisplay(sheet.totalOvertimeMinutes)}
                                                    </span>
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                            <td className="text-right font-mono text-sm">
                                                {sheet.totalNightMinutes > 0 ? (
                                                    <span className="text-info font-semibold">
                                                        {minutesToDisplay(sheet.totalNightMinutes)}
                                                    </span>
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                            <td className="text-center">
                                                {sheet.totalAbsenceDays > 0 ? (
                                                    <span className="badge badge-error badge-sm">
                                                        {sheet.totalAbsenceDays}
                                                    </span>
                                                ) : (
                                                    <span className="text-base-content/40">0</span>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                {sheet.totalLateDays > 0 ? (
                                                    <span className="badge badge-warning badge-sm">
                                                        {sheet.totalLateDays}
                                                    </span>
                                                ) : (
                                                    <span className="text-base-content/40">0</span>
                                                )}
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge ${STATUS_BADGES[sheet.status] || 'badge-ghost'}`}
                                                >
                                                    {STATUS_LABELS[sheet.status] || sheet.status}
                                                </span>
                                            </td>
                                            <td className="text-sm">
                                                {sheet.approvedBy?.name || '-'}
                                            </td>
                                            <td>
                                                <div className="flex items-center gap-1 justify-end">
                                                    {canUpdate && sheet.status === 'OPEN' && (
                                                        <button
                                                            className="btn btn-ghost btn-xs gap-1"
                                                            title="Fechar folha de ponto"
                                                            onClick={() =>
                                                                setConfirmAction({
                                                                    type: 'close',
                                                                    id: sheet.id,
                                                                    title: 'Fechar Folha de Ponto',
                                                                    message: `Deseja fechar a folha de ponto de ${sheet.employee?.name} referente a ${String(sheet.month).padStart(2, '0')}/${sheet.year}? Os totais serão calculados automaticamente.`,
                                                                })
                                                            }
                                                        >
                                                            <MdLock className="text-lg" />
                                                        </button>
                                                    )}
                                                    {canUpdate && sheet.status === 'CLOSED' && (
                                                        <button
                                                            className="btn btn-ghost btn-xs text-success gap-1"
                                                            title="Aprovar folha de ponto"
                                                            onClick={() =>
                                                                setConfirmAction({
                                                                    type: 'approve',
                                                                    id: sheet.id,
                                                                    title: 'Aprovar Folha de Ponto',
                                                                    message: `Deseja aprovar a folha de ponto de ${sheet.employee?.name} referente a ${String(sheet.month).padStart(2, '0')}/${sheet.year}?`,
                                                                })
                                                            }
                                                        >
                                                            <MdCheck className="text-lg" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {timeSheets.length === 0 && !sheetsLoading && (
                        <div className="text-center py-8 text-base-content/50">
                            Nenhuma folha de ponto encontrada. Selecione o período e clique em Consultar.
                        </div>
                    )}
                </div>
            </div>

            {/* Confirmação */}
            <ConfirmModal
                isOpen={!!confirmAction}
                onClose={() => setConfirmAction(null)}
                onConfirm={
                    confirmAction?.type === 'close'
                        ? handleCloseSheet
                        : handleApproveSheet
                }
                title={confirmAction?.title || ''}
                message={confirmAction?.message || ''}
                loading={confirmLoading}
                confirmButtonText={
                    confirmAction?.type === 'close' ? 'Fechar' : 'Aprovar'
                }
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
